import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import type { MediaAgentPoolMemberDto } from '@/types/dtos/media-agent/media-agent-pool.dto';
import { AGENT_POOLS_LIVE_REFETCH_MS } from '@/state/queries/agent-pools-live.constants';
import { hasMediaAgentAuthIssue, isMediaAgentHealStuck } from '@/helpers/media-agent-automation.helper';

export const LIST_MEDIA_AGENT_POOL_QUERY_KEY = 'media-agent-pool';

export function filterMediaAgentsByStatus(agents: MediaAgentPoolMemberDto[], status: string) {
  if (!status) return agents;
  switch (status) {
    case 'connected':
      return agents.filter((a) => a.isConnected && a.opsStatus === 'active');
    case 'offline':
      return agents.filter((a) => !a.isConnected && a.opsStatus === 'active');
    case 'allocated':
      return agents.filter((a) => Boolean(a.allocatedFlowId));
    case 'free':
      return agents.filter((a) => !a.allocatedFlowId && !a.currentBookingId && a.opsStatus === 'active');
    case 'maintenance':
      return agents.filter((a) => a.opsStatus === 'maintenance' || a.maintenanceMode);
    case 'paused':
      return agents.filter((a) => a.opsStatus === 'paused');
    case 'auth':
      return agents.filter((a) => hasMediaAgentAuthIssue(a) || isMediaAgentHealStuck(a));
    default:
      return agents;
  }
}

export function useListMediaAgentPoolQuery({ enabled = true }: { enabled?: boolean } = {}) {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: [LIST_MEDIA_AGENT_POOL_QUERY_KEY],
    enabled,
    queryFn: async () => {
      const token = (await getToken()) ?? '';
      const url = `${import.meta.env.VITE_API_URL}/media-agent/pool`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to load media agent pool');
      const json = (await res.json()) as { agents: MediaAgentPoolMemberDto[] };
      return json.agents;
    },
    refetchInterval: enabled ? AGENT_POOLS_LIVE_REFETCH_MS : false,
  });
}
